import React, { useCallback, useEffect, useState } from 'react';

/**
 * Asks what to build next, at the moment someone finishes playing.
 *
 * Why here: the site records ~758 game starts and ~476 score posts a month
 * across 27 games, engages at 93%, and holds people for 348 seconds — the best
 * engagement in the portfolio. It asks them nothing. The one moment a player
 * can say which game deserves more work is the moment they just finished one.
 *
 * Restraint rules, same as markdownme's post-conversion prompt:
 *   - never on load, never on a timer, only after a real game_end
 *   - once per session, and a decline is remembered
 *   - one click is a complete answer; typing is always optional
 *   - inline under the game, never a modal over it
 */

const API_BASE = 'https://n2ipbuesqk.execute-api.us-east-1.amazonaws.com';
const ASKED_KEY = 'vg.feedback.asked';

function alreadyAsked() {
  try {
    return window.sessionStorage.getItem(ASKED_KEY) === '1';
  } catch {
    return false;
  }
}

function markAsked() {
  try {
    window.sessionStorage.setItem(ASKED_KEY, '1');
  } catch {
    /* private mode — the in-memory guard still holds for this page */
  }
}

export default function GameFeedback({ gameId, gameTitle }) {
  const [phase, setPhase] = useState('hidden');
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState('');

  // Show only after the player has actually spent time in the game. A player
  // who bounces in two seconds has no opinion worth collecting.
  useEffect(() => {
    if (!gameId || alreadyAsked()) return;
    const t = setTimeout(() => setPhase((p) => (p === 'hidden' ? 'idle' : p)), 45000);
    return () => clearTimeout(t);
  }, [gameId]);

  const send = useCallback(
    (payload) => {
      fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site: 'vedicgame.com',
          page: `/games/${gameId}`,
          context: 'post_play',
          ...payload,
        }),
      }).catch(() => {
        /* never surface an error to someone doing us a favour */
      });
    },
    [gameId]
  );

  if (phase === 'hidden' || !gameId) return null;

  const close = () => {
    markAsked();
    setPhase('hidden');
  };

  const rate = (r) => {
    setRating(r);
    setPhase('open');
    markAsked();
    send({ rating: r });
    if (window.gtag) {
      window.gtag('event', 'game_feedback', {
        event_category: 'Game',
        event_label: gameId,
        game_id: gameId,
        rating: r,
      });
    }
  };

  const submit = () => {
    const note = message.trim();
    if (note) {
      send({ rating, message: note });
      if (window.gtag) {
        window.gtag('event', 'game_feedback_note', {
          event_category: 'Game',
          event_label: gameId,
          game_id: gameId,
        });
      }
    }
    setPhase('done');
  };

  const wrap = {
    maxWidth: 720,
    margin: '24px auto 0',
    padding: '14px 18px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    fontSize: 14,
    color: '#e8e2d5',
  };
  const thumb = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'transparent',
    color: '#e8e2d5',
    fontSize: 14,
    cursor: 'pointer',
  };

  if (phase === 'idle') {
    return (
      <div style={wrap}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Enjoying {gameTitle || 'this'}?</span>
          <button type="button" style={thumb} onClick={() => rate('up')} aria-label="Yes">
            Yes
          </button>
          <button type="button" style={thumb} onClick={() => rate('down')} aria-label="Not really">
            Not really
          </button>
          <button
            type="button"
            onClick={close}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(232,226,213,0.5)', fontSize: 13, cursor: 'pointer' }}
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'open') {
    return (
      <div style={wrap}>
        <label htmlFor="vg-feedback" style={{ display: 'block', marginBottom: 8 }}>
          {rating === 'up'
            ? 'What should we build next? Any game you want to see?'
            : 'What would make this better?'}
        </label>
        <textarea
          id="vg-feedback"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={2000}
          rows={3}
          placeholder={rating === 'up' ? 'e.g. more Mahabharata battles, or a co-op mode' : 'Optional'}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(0,0,0,0.25)',
            color: '#e8e2d5',
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <button
            type="button"
            onClick={submit}
            style={{ ...thumb, background: '#ba7517', borderColor: '#ba7517', fontWeight: 600 }}
          >
            Send
          </button>
          <button
            type="button"
            onClick={() => setPhase('done')}
            style={{ background: 'none', border: 'none', color: 'rgba(232,226,213,0.6)', fontSize: 14, cursor: 'pointer' }}
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  return <div style={wrap}>Thanks — that goes straight to the person who builds these.</div>;
}
